import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Row, Col, Badge } from 'react-bootstrap';
import TickerUpdateMonitor from './TickerUpdateMonitor';

const POLLING_INTERVAL_OPTIONS = [60, 120, 180];

const HeartbeatIndicator = ({ 
  lastUpdateTime, 
  updateIntervalMinutes = 1,
  isActive = true,
  socketStatus = 'connected',
  size = 20,
  strokeWidth,
  // Distributed system status
  distributedStatus = 'idle', // 'idle', 'acquiring_lock', 'rate_limited', 'updating', 'market_closed'
  lockStatus = null,
  rateLimitStatus = null,
  // Dual-channel update status
  isWebSocketConnected = true,
  isPollingActive = false,
  lastUpdateSource = null,
  totalUpdates = 0,
  // Extended dual-channel details and controls for monitor
  webSocketUpdates = 0,
  pollingUpdates = 0,
  pollingStatus = null,
  setPollingInterval
}) => {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [selectedIntervalSeconds, setSelectedIntervalSeconds] = useState(() => {
    const interval = pollingStatus?.pollingIntervalMs;
    return interval ? Math.round(interval / 1000) : POLLING_INTERVAL_OPTIONS[0];
  });
  const intervalRef = useRef(null);

  // Calculate progress (0.0 => 0% up to 1.0 => 100%) based on poll cycle (preferred) or last update fallback
  const updateProgress = useCallback(() => {
    if (!isActive) {
      setProgress(0);
      setTimeRemaining(updateIntervalMinutes * 60);
      return;
    }

    const now = new Date();

    // Prefer polling cycle if active and interval is known
    const intervalMs = pollingStatus?.pollingIntervalMs;
    const lastPollTime = pollingStatus?.lastPollTime ? new Date(pollingStatus.lastPollTime) : null;

    if (isPollingActive && intervalMs) {
      const totalSeconds = Math.max(1, Math.floor(intervalMs / 1000));
      const cycleStart = lastPollTime || (lastUpdateTime ? new Date(lastUpdateTime) : now);
      const elapsedSeconds = Math.max(0, Math.floor((now - cycleStart) / 1000));
      const clampedElapsed = Math.min(elapsedSeconds, totalSeconds);

      const progressValue = Math.min(Math.max(clampedElapsed / totalSeconds, 0), 1);
      setProgress(progressValue);
      setTimeRemaining(Math.max(totalSeconds - clampedElapsed, 0));
      return;
    }

    // Fallback: use lastUpdateTime and configured updateIntervalMinutes
    if (!lastUpdateTime) {
      setProgress(0);
      setTimeRemaining(updateIntervalMinutes * 60);
      return;
    }

    const lastUpdate = new Date(lastUpdateTime);
    const elapsedSeconds = Math.floor((now - lastUpdate) / 1000);
    const totalSeconds = updateIntervalMinutes * 60;
    const clampedElapsed = Math.min(elapsedSeconds, totalSeconds);
    const progressValue = Math.min(Math.max(clampedElapsed / totalSeconds, 0), 1);
    setProgress(progressValue);
    setTimeRemaining(Math.max(totalSeconds - clampedElapsed, 0));
  }, [isActive, updateIntervalMinutes, pollingStatus, isPollingActive, lastUpdateTime]);

  // Start/stop progress updates
  useEffect(() => {
    if (isActive && socketStatus === 'connected') {
      // Update immediately
      updateProgress();

      // Then update every second
      intervalRef.current = setInterval(updateProgress, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, socketStatus, updateProgress]);

  // Recalculate when lastUpdateTime or polling status changes
  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  // Get colors based on distributed system state
  const getColors = () => {
    // First check distributed system status
    if (distributedStatus === 'acquiring_lock') {
      return {
        background: '#0d6efd',
        progress: '#0d6efd',
        glow: '#0d6efd50'
      };
    } else if (distributedStatus === 'rate_limited') {
      return {
        background: '#fd7e14',
        progress: '#fd7e14',
        glow: '#fd7e1450'
      };
    } else if (distributedStatus === 'updating') {
      return {
        background: '#198754',
        progress: '#198754',
        glow: '#19875450'
      };
    } else if (distributedStatus === 'market_closed') {
      return {
        background: '#6c757d',
        progress: '#6c757d',
        glow: '#6c757d50'
      };
    }
    
    // Fall back to socket status
    if (socketStatus === 'disconnected') {
      return {
        background: '#dc3545',
        progress: '#dc3545',
        glow: '#dc354550'
      };
    } else if (socketStatus === 'connecting') {
      return {
        background: '#ffc107',
        progress: '#ffc107', 
        glow: '#ffc10750'
      };
    } else if (!isActive) {
      return {
        background: '#6c757d',
        progress: '#6c757d',
        glow: '#6c757d50'
      };
    } else {
      return {
        background: '#198754',
        progress: '#198754',
        glow: '#19875450'
      };
    }
  };

  const colors = getColors();
  // Calculate stroke width as 50% of the radius if not provided
  const calculatedStrokeWidth = strokeWidth || (size * 0.25); // 50% of radius (radius = size/2, so 50% of radius = size/4)
  const radius = (size - calculatedStrokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress * circumference);

  useEffect(() => {
    const interval = pollingStatus?.pollingIntervalMs;
    if (!interval) {
      return;
    }

    const intervalSeconds = Math.round(interval / 1000);
    if (!Number.isNaN(intervalSeconds) && intervalSeconds !== selectedIntervalSeconds) {
      setSelectedIntervalSeconds(intervalSeconds);
    }
  }, [pollingStatus?.pollingIntervalMs, selectedIntervalSeconds]);

  const handlePollingIntervalChange = (seconds) => {
    if (seconds === selectedIntervalSeconds) {
      return;
    }

    setSelectedIntervalSeconds(seconds);

    if (typeof setPollingInterval === 'function') {
      setPollingInterval(seconds * 1000);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds) => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  // Handle click with animation
  const handleClick = () => {
    setIsClicked(true);
    setShowModal(true);
    // Reset animation after 1 second
    setTimeout(() => setIsClicked(false), 1000);
  };

  // Animation classes for different states
  const getAnimationClass = () => {
    if (isClicked) {
      return 'heartbeat-clicked';
    } else if (socketStatus === 'connecting') {
      return 'heartbeat-connecting';
    } else if (socketStatus === 'disconnected') {
      return 'heartbeat-disconnected';
    } else if (progress >= 1) {
      return 'heartbeat-complete';
    }
    return '';
  };

  // Format time for display
  const formatTime = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleTimeString();
  };

  // Get status badge variant based on distributed system state
  const getStatusVariant = () => {
    if (distributedStatus === 'acquiring_lock') return 'primary';
    if (distributedStatus === 'rate_limited') return 'warning';
    if (distributedStatus === 'updating') return 'success';
    if (distributedStatus === 'market_closed') return 'secondary';
    
    // Fall back to socket status
    if (socketStatus === 'disconnected') return 'danger';
    if (socketStatus === 'connecting') return 'warning';
    if (!isActive) return 'secondary';
    return 'success';
  };

  // Get status text based on distributed system state
  const getStatusText = () => {
    if (distributedStatus === 'acquiring_lock') return 'Acquiring Lock';
    if (distributedStatus === 'rate_limited') return 'Rate Limited';
    if (distributedStatus === 'updating') return 'Updating';
    if (distributedStatus === 'market_closed') return 'Market Closed';
    
    // Fall back to socket status
    if (socketStatus === 'disconnected') return 'Disconnected';
    if (socketStatus === 'connecting') return 'Connecting';
    if (!isActive) return 'Market Closed';
    return 'Active';
  };

  // Get detailed status description
  const getStatusDescription = () => {
    if (distributedStatus === 'acquiring_lock') {
      return 'Waiting to acquire distributed lock for ticker updates';
    } else if (distributedStatus === 'rate_limited') {
      return 'Waiting for rate limit reset before next update';
    } else if (distributedStatus === 'updating') {
      return 'Currently updating ticker prices';
    } else if (distributedStatus === 'market_closed') {
      return 'Market is closed - no updates scheduled';
    }
    
    // Fall back to socket status
    if (socketStatus === 'disconnected') return 'Connection lost - updates paused';
    if (socketStatus === 'connecting') return 'Establishing connection...';
    if (!isActive) return 'Market closed - no price updates';
    return 'Real-time updates active';
  };

  return (
    <>
      <style>
        {`
          .heartbeat-indicator {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: transform 0.2s ease;
          }
          
          .heartbeat-indicator:hover {
            transform: scale(1.05);
          }
          
          .heartbeat-svg {
            transform: rotate(-90deg);
            filter: drop-shadow(0 0 2px var(--glow-color));
          }
          
          .heartbeat-connecting {
            animation: heartbeat-pulse 1.5s ease-in-out infinite;
          }
          
          .heartbeat-disconnected {
            animation: heartbeat-error 1s ease-in-out infinite;
          }
          
          .heartbeat-complete {
            animation: heartbeat-ready 0.5s ease-in-out;
          }
          
          .heartbeat-clicked {
            animation: heartbeat-click 1s ease-out;
          }
          
          @keyframes heartbeat-pulse {
            0%, 100% { 
              transform: rotate(-90deg) scale(1);
              opacity: 1;
            }
            50% { 
              transform: rotate(-90deg) scale(1.1);
              opacity: 0.7;
            }
          }
          
          @keyframes heartbeat-error {
            0%, 100% { 
              transform: rotate(-90deg) scale(1);
            }
            25%, 75% { 
              transform: rotate(-90deg) scale(0.95);
            }
            50% { 
              transform: rotate(-90deg) scale(1.05);
            }
          }
          
          @keyframes heartbeat-ready {
            0% { 
              transform: rotate(-90deg) scale(1);
            }
            50% { 
              transform: rotate(-90deg) scale(1.15);
              filter: drop-shadow(0 0 6px var(--glow-color));
            }
            100% { 
              transform: rotate(-90deg) scale(1);
            }
          }
          
          @keyframes heartbeat-click {
            0% { 
              transform: rotate(-90deg) scale(1);
              filter: drop-shadow(0 0 2px var(--glow-color));
            }
            25% { 
              transform: rotate(-90deg) scale(1.3);
              filter: drop-shadow(0 0 8px var(--glow-color));
            }
            50% { 
              transform: rotate(-90deg) scale(1.5);
              filter: drop-shadow(0 0 12px var(--glow-color));
            }
            75% { 
              transform: rotate(-90deg) scale(1.2);
              filter: drop-shadow(0 0 6px var(--glow-color));
            }
            100% { 
              transform: rotate(-90deg) scale(1);
              filter: drop-shadow(0 0 2px var(--glow-color));
            }
          }
          
          .heartbeat-time {
            position: absolute;
            font-size: ${size * 0.25}px;
            font-weight: 600;
            color: ${colors.progress};
            text-align: center;
            line-height: 1;
            pointer-events: none;
            font-family: 'Courier New', monospace;
          }
        `}
      </style>
      
      <div 
        className={`heartbeat-indicator ${getAnimationClass()}`}
        onClick={handleClick}
        title={
          !lastUpdateTime 
            ? 'Waiting for first price update... (Click for details)'
            : !isActive 
              ? 'Market closed - no price updates (Click for details)'
              : `Next price update: ${formatTimeRemaining(timeRemaining)} | Progress: ${Math.round(progress * 100)}% (Click for details)`
        }
      >
        <svg
          className="heartbeat-svg"
          width={size}
          height={size}
          style={{ '--glow-color': colors.glow }}
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.background}
            strokeWidth={calculatedStrokeWidth}
            opacity={0.3}
          />
          
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.progress}
            strokeWidth={calculatedStrokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.3s ease-in-out'
            }}
          />
        </svg>
        
        {/* Time remaining text (only show if space allows) */}
        {size >= 32 && (
          <div className="heartbeat-time">
            {timeRemaining <= 60 ? `${timeRemaining}` : `${Math.floor(timeRemaining / 60)}`}
          </div>
        )}
      </div>

      {/* Cycle Information Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        backdrop={true}
        keyboard={true}
        style={{
          maxWidth: '1200px',
          width: '1200px',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          margin: '0'
        }}
      >
        <Modal.Header 
          style={{ padding: '0.5rem 1rem', borderBottom: 'none' }}
        >
          <Modal.Title className="text-center w-100" style={{ fontSize: '1.2rem', fontWeight: 'bold', color: colors.progress }}>
            {formatTimeRemaining(timeRemaining)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ padding: '0.5rem', position: 'relative' }}
        >
          <Row className="g-4 align-items-stretch" style={{ minHeight: '500px' }}>
            {/* Left Column: Progress Ring */}
            <Col md={6} className="d-flex flex-column" style={{ height: '100%' }}>
              <div
                className="text-center"
                style={{
                  position: 'relative',
                  width: '100%',
                  flex: '1 1 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    paddingTop: '100%'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    viewBox="0 0 400 400"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      transform: 'rotate(-90deg)'
                    }}
                  >
                    {/* Background circle */}
                    <circle
                      cx="200"
                      cy="200"
                      r="160"
                      fill="none"
                      stroke={colors.background}
                      strokeWidth="40"
                      opacity="0.3"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="200"
                      cy="200"
                      r="160"
                      fill="none"
                      stroke={colors.progress}
                      strokeWidth="40"
                      strokeDasharray={2 * Math.PI * 160}
                      strokeDashoffset={2 * Math.PI * 160 - (progress * 2 * Math.PI * 160)}
                      strokeLinecap="round"
                      style={{
                        transition: 'stroke-dashoffset 0.3s ease-in-out'
                      }}
                    />
                  </svg>
                </div>

                  {/* All content positioned inside the ring */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: '70%',
                        maxWidth: '320px',
                        textAlign: 'center'
                      }}
                    >
                    {/* Main countdown display */}
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.progress, marginBottom: '1rem' }}>
                      {Math.round(progress * 100)}%
                    </div>

                    {/* Status badge */}
                    <div style={{ marginBottom: '1rem' }}>
                      <Badge bg={getStatusVariant()} style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
                        {getStatusText()}
                      </Badge>
                    </div>

                    {/* Status description */}
                    <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '1rem', padding: '0 0.5rem', lineHeight: '1.3' }}>
                      {getStatusDescription()}
                    </div>

                    {/* Additional status information */}
                    {(lockStatus || rateLimitStatus) && (
                      <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '1rem' }}>
                        {lockStatus && (
                          <div className="mb-1">
                            <span className="text-muted">Lock:</span> {lockStatus}
                          </div>
                        )}
                        {rateLimitStatus && (
                          <div>
                            <span className="text-muted">Rate Limit:</span> {rateLimitStatus}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dual-Channel Update Status */}
                    <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '1rem' }}>
                      <div className="mb-2">
                        <span className="text-muted">Update Channels:</span>
                      </div>
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="d-flex align-items-center">
                          <span className={`me-1 ${isWebSocketConnected ? 'text-success' : 'text-warning'}`}>
                            {isWebSocketConnected ? '✓' : '✗'}
                          </span>
                          <span className="text-muted small">WebSocket</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className={`me-1 ${isPollingActive ? 'text-info' : 'text-muted'}`}>
                            {isPollingActive ? '✓' : '✗'}
                          </span>
                          <span className="text-muted small">Polling</span>
                        </div>
                      </div>
                      {lastUpdateSource && (
                        <div className="mb-1">
                          <span className="text-muted">Last Source:</span>
                          <Badge
                            bg={lastUpdateSource === 'webSocket' ? 'success' : lastUpdateSource === 'polling' ? 'info' : 'secondary'}
                            className="ms-1"
                            style={{ fontSize: '0.6rem' }}
                          >
                            {lastUpdateSource}
                          </Badge>
                        </div>
                      )}
                      {totalUpdates > 0 && (
                        <div>
                          <span className="text-muted">Total Updates:</span>
                          <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                            {totalUpdates}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Last Update */}
                    <div style={{ fontSize: '0.9rem' }}>
                      <div className="text-muted small mb-1">Last Update</div>
                      <div className="text-dark fw-bold">{formatTime(lastUpdateTime)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column: Update Monitor */}
            <Col md={6}>
              <div style={{ height: '100%', overflow: 'hidden' }}>
                <TickerUpdateMonitor
                  show={showModal}
                  isWebSocketConnected={isWebSocketConnected}
                  isPollingActive={isPollingActive}
                  lastUpdateSource={lastUpdateSource}
                  totalUpdates={totalUpdates}
                  webSocketUpdates={webSocketUpdates}
                  pollingUpdates={pollingUpdates}
                  lastUpdateTime={lastUpdateTime}
                  pollingStatus={pollingStatus}
                  pollingIntervalOptions={POLLING_INTERVAL_OPTIONS}
                  selectedIntervalSeconds={selectedIntervalSeconds}
                  onPollingIntervalChange={handlePollingIntervalChange}
                  eventLogSize={25}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default HeartbeatIndicator;
